import { api } from '@/lib/axios';

/**
 * AP-06-B Regel 6 — vier Briefpapier-Plätze: je Ausrichtung ein Blatt für die
 * erste Seite und eines für die Folgeseiten.
 */
export type LetterheadSlot = 'portrait' | 'portrait-follow' | 'landscape' | 'landscape-follow';

/** Welches Feld in `TenantBranding` der jeweilige Platz belegt. */
export const LETTERHEAD_SLOT_FIELDS = {
  'portrait': 'letterheadPdfPath',
  'portrait-follow': 'letterheadFollowPath',
  'landscape': 'letterheadLandscapePdfPath',
  'landscape-follow': 'letterheadLandscapeFollowPath',
} as const satisfies Record<LetterheadSlot, keyof TenantBranding>;

export interface TenantBranding {
  id: string;
  tenantId: string;
  /** Hochformat Seite 1 (Namens-Altlast: hält einen PNG-Schlüssel). */
  letterheadPdfPath?: string | null;
  /** Hochformat Folgeseiten. */
  letterheadFollowPath?: string | null;
  /** Querformat Seite 1. */
  letterheadLandscapePdfPath?: string | null;
  /** Querformat Folgeseiten. */
  letterheadLandscapeFollowPath?: string | null;
  logoPath?: string | null;
  /** AP-06-B Regel 3 — Standard-Ausrichtung der Exporte. */
  pageOrientationMode?: 'auto' | 'portrait' | 'landscape' | null;
  /** Ab wie vielen Spalten 'auto' quer druckt. */
  autoLandscapeFromColumns?: number | null;
  /** Wie ein PDF beim Benutzer ankommt: als Download oder in einem neuen Tab. */
  exportPdfDelivery?: 'download' | 'tab' | null;
  marginTopMm?: number | null;
  marginBottomMm?: number | null;
  marginLeftMm?: number | null;
  marginRightMm?: number | null;
  logoAlign?: 'left' | 'center' | 'right' | null;
  logoOffsetXmm?: number | null;
  logoOffsetYmm?: number | null;
  logoWidthMm?: number | null;
  companyName?: string | null;
  companyStreet?: string | null;
  companyCity?: string | null;
  companyCountry?: string | null;
  companyPhone?: string | null;
  companyEmail?: string | null;
  companyWeb?: string | null;
  companyUid?: string | null;
  companyIban?: string | null;
  companyBic?: string | null;
  companyBankName?: string | null;
  fontFamily: string;
  fontSizes: { h1: number; h2: number; tableHeader: number; tableBody: number; tableSum: number; footer: number };
  accentColors: string[];
  zebraEnabled: boolean;
  zebraColor: string;
  filenameTemplate: string;
}

export type UpdateBrandingPayload = Partial<Omit<TenantBranding, 'id' | 'tenantId'>>;

export const brandingApi = {
  get: () =>
    api.get<TenantBranding | null>('/branding'),

  update: (payload: UpdateBrandingPayload) =>
    api.patch<TenantBranding>('/branding', payload),

  uploadLogo: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<{ logoPath: string }>('/branding/logo', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  deleteLogo: () =>
    api.delete('/branding/logo'),

  /** Ein Endpunktpaar für alle vier Briefpapier-Plätze. */
  uploadLetterhead: (slot: LetterheadSlot, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<Partial<TenantBranding>>(`/branding/letterhead/${slot}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  deleteLetterhead: (slot: LetterheadSlot) =>
    api.delete(`/branding/letterhead/${slot}`),
};
